/**
 * Feeding: receiving cattle into pens and ownership lots, rations and feed stock, bunk calls, mixer
 * loads and deliveries, and lot closeouts with owner statements. Pens are places, lots are groups of
 * kind "lot"; a lot's counted head and where it sits are kept apart from who owns it.
 */
import { addDays, defineEvent, defineFacet, defineModule, f, msg, type Badge, type Fact, type Fields, type Formatters, type SubjectRef } from "../../contract";
import { daysOnFeed, toLb } from "./shared/math";
import {
  reduceAnimal, reduceLot, reducePen,
  type AnimalFeedState, type BunkScore, type CallData, type DeliveryData, type HeadOutData, type LoadData, type LotMoveData, type LotState, type PenState, type ReceiveData,
} from "./shared/state";

export const BUNK_SCORES: Record<BunkScore, string> = {
  slick: "Slick (0)",
  crumbs: "Crumbs (½)",
  thin: "Thin layer (1)",
  half: "About half left (2)",
  heavy: "Most left (3)",
  untouched: "Untouched (4)",
};

export const OUT_REASONS: Record<HeadOutData["reason"], string> = {
  shipped: "Shipped or sold",
  died: "Died",
  transferred: "Moved to another lot or place",
  missing: "Missing on a recount",
};

export const SKIP_REASONS: Record<NonNullable<DeliveryData["skip_reason"]>, string> = {
  equipment: "Equipment broke down",
  weather: "Weather",
  pen_empty: "Pen was empty",
  other: "Other reason",
};

const PRICE_BASIS = { per_cwt: "Per hundredweight", per_head: "Per head", total: "Total" } as const;

const label = (subjects: SubjectRef[], role: string, x: Formatters) =>
  x.list(subjects.filter((s) => s.role === role).map((s) => s.label ?? x.label(s.id)));
const heads = (n: number, x: Formatters) => x.count(n, "One head", "{{count}} head");

// ---------------------------------------------------------------- receiving

const receive = defineEvent<ReceiveData>({
  type: "feeding.receive",
  label: "Cattle received",
  description: "Cattle arrived into a feeding or stocker lot and its pens, with the count against what was expected and the pay weight.",
  icon: "feedReceive",
  tone: "feed",
  subjects: [
    { role: "lot", kind: "group", label: "Lot", required: true, help: "The ownership or production lot these cattle belong to" },
    { role: "pen", kind: "place", label: "Pens", many: true, required: true },
    { role: "seller", kind: "contact", label: "Seller" },
    { role: "animal", kind: "animal", label: "Tagged cattle", many: true, help: "Only cattle you can identify. The rest stay counted, not invented." },
  ],
  fields: {
    head_received: f.int("Head off the truck", { required: true, min: 0, agent: "Head that actually arrived. Never add head that didn't come." }),
    head_expected: f.int("Head expected", { min: 0, agent: "Head bought or shipped, when said. A difference is a shortage to settle, not a death." }),
    pay_weight: f.quantity("Pay weight per head", "weight", { help: "Average pay weight a head, after shrink", agent: "Average pay weight per head. \"Pay weight 612\" for a truck of calves means 612 lb a head." }),
    pen_split: f.list("Head by pen", f.object("Pen", { pen_id: f.ref("Pen", "place", { required: true }), head: f.int("Head", { required: true, min: 0 }) }), {
      level: "more", itemLabel: "Pen", help: "Needed when the cattle went to more than one pen", agent: "Only when they named more than one pen. The heads must add up to head off the truck.",
    }),
    pay_weight_total: f.quantity("Pay weight, whole load", "weight", { level: "more" }),
    gross_weight_total: f.quantity("Gross weight on the ticket", "weight", { level: "more" }),
    shrink_pct: f.number("Shrink", { level: "more", min: 0, max: 30, suffix: "%", help: "Pencil shrink used to figure pay weight" }),
    arrival_weight: f.quantity("Weight here, per head", "weight", { level: "more", help: "Our own scale on arrival, kept apart from pay weight" }),
    price: f.money("Price", { level: "more" }),
    price_basis: f.enum("Price is", PRICE_BASIS, { level: "more" }),
    owners: f.list("Owners", f.object("Owner", { contact_id: f.ref("Owner", "contact", { required: true }), share: f.number("Ownership share", { required: true, min: 0, max: 100, suffix: "%" }) }), {
      level: "more", itemLabel: "Owner", help: "Shares should add up to 100%", agent: "Only owners and shares they stated.",
    }),
    origin: f.text("Origin", { level: "more", placeholder: "Sale barn, town or ranch" }),
    ticket: f.text("Scale ticket or invoice", { level: "more" }),
    shortage_reason: f.enum("Why short", { short_shipped: "Seller shipped fewer", dead_on_arrival: "Dead on arrival", unknown: "Not sorted out yet" }, {
      level: "more", agent: "Only when they said why. Leave empty otherwise.",
    }),
    risk: f.enum("Health risk", { low: "Low", medium: "Medium", high: "High" }, { level: "more" }),
    target_days: f.int("Days on feed planned", { level: "more", min: 1, max: 500 }),
    process_on: f.date("Process on", { level: "more", help: "Makes a task to work them through the chute" }),
    processing: f.longText("Processing plan", { level: "more", help: "What they'll get. Treatments are recorded when they are actually given." }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const parts = [x.t("{{lot}}: {{head}} into {{pens}}", { lot: label(e.subjects, "lot", x), head: heads(d.head_received, x), pens: label(e.subjects, "pen", x) })];
    const short = (d.head_expected ?? d.head_received) - d.head_received;
    if (short > 0) parts.push(x.t("{{short}} short of {{expected}}", { short, expected: d.head_expected ?? 0 }));
    if (short < 0) parts.push(x.t("{{over}} over the {{expected}} expected", { over: -short, expected: d.head_expected ?? 0 }));
    if (d.pay_weight) parts.push(x.t("pay {{weight}}", { weight: x.quantity(d.pay_weight) }));
    const seller = label(e.subjects, "seller", x);
    if (seller) parts.push(x.t("from {{seller}}", { seller }));
    return parts.join(" · ");
  },
  review: "always",
  agent: {
    when: "Cattle arrived on a truck into a feeding or stocker lot: head counts, pens, pay weight, seller. Create the lot (a group of kind lot) first if it is new.",
    avoid: "Not for moving our own cattle between pastures (a move) or for buying cattle that haven't arrived. A shortage is not a death.",
    examples: ["received 84 head from Davis, 82 off the truck, pay weight 612", "truck from Davis is here, 82 head, two short; 50 to pen six and 32 to seven"],
  },
  effects: {
    followUps: (d, on, subjects) => {
      const lot = subjects.find((s) => s.role === "lot");
      const out = [];
      const short = (d.head_expected ?? d.head_received) - d.head_received;
      if (short > 0 && on) out.push({ key: "shortage", title: msg("Settle the receiving shortage: {{count}} head short", { count: short }), due_at: addDays(on, 2), subject: lot ? { kind: "group" as const, id: lot.id } : undefined });
      if (d.process_on) out.push({ key: "process", title: msg("Process the cattle received {{date}}", { date: on ?? d.process_on }), due_at: d.process_on, subject: lot ? { kind: "group" as const, id: lot.id } : undefined });
      return out;
    },
  },
  validate: (d, subjects) => {
    const issues = [];
    const pens = subjects.filter((s) => s.role === "pen").map((s) => s.id);
    if (pens.length > 1 && !d.pen_split?.length) issues.push({ path: "data.pen_split", message: "Say how many head went to each pen" });
    if (d.pen_split?.length) {
      const sum = d.pen_split.reduce((n, s) => n + s.head, 0);
      if (sum !== d.head_received) issues.push({ path: "data.pen_split", message: "Head by pen must add up to head off the truck" });
      if (d.pen_split.some((s) => !pens.includes(s.pen_id))) issues.push({ path: "data.pen_split", message: "Every pen in the split must be one of the pens" });
    }
    if (subjects.filter((s) => s.role === "animal").length > d.head_received) issues.push({ path: "subjects.animal", message: "More tagged cattle than head off the truck" });
    return issues;
  },
});

// ---------------------------------------------------------------- lot inventory

const lotMove = defineEvent<LotMoveData>({
  type: "feeding.lot_move",
  label: "Lot moved between pens",
  description: "Counted head of a lot moved from one pen to another. Ownership and the lot's costs move with them.",
  icon: "feedMove",
  tone: "feed",
  subjects: [
    { role: "lot", kind: "group", label: "Lot", required: true },
    { role: "from", kind: "place", label: "From pen", required: true },
    { role: "to", kind: "place", label: "To pen", required: true },
  ],
  fields: {
    head: f.int("Head", { required: true, min: 1 }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => x.t("Moved {{head}} of {{lot}} from {{from}} to {{to}}", {
    head: heads(e.data.head, x), lot: label(e.subjects, "lot", x), from: label(e.subjects, "from", x), to: label(e.subjects, "to", x),
  }),
  agent: {
    when: "Counted head of a feeding or stocker lot moved between pens.",
    avoid: "Not for tagged cattle moving between pastures; use the move record for those.",
    examples: ["moved 30 of the Davis steers from pen 1 to pen 3"],
  },
  validate: (_d, subjects) => {
    const from = subjects.find((s) => s.role === "from")?.id;
    const to = subjects.find((s) => s.role === "to")?.id;
    return from && from === to ? [{ path: "subjects.to", message: "Pick a different pen to move them to" }] : [];
  },
});

const headOut = defineEvent<HeadOutData>({
  type: "feeding.head_out",
  label: "Head out of a lot",
  description: "Counted head left a lot: shipped or sold, died, moved to another lot, or came up missing on a recount.",
  icon: "feedOut",
  tone: "feed",
  subjects: [
    { role: "lot", kind: "group", label: "Lot", required: true },
    { role: "pen", kind: "place", label: "Pen" },
    { role: "to", kind: "contact", label: "Buyer or destination" },
    { role: "animal", kind: "animal", label: "Tagged cattle", many: true },
  ],
  fields: {
    head: f.int("Head", { required: true, min: 1 }),
    reason: f.enum("Why", OUT_REASONS, { required: true, agent: "A count that came up short is missing, never shipped or died unless they said so." }),
    pay_weight: f.quantity("Pay weight per head", "weight", { help: "For shipments: average pay weight a head" }),
    pay_weight_total: f.quantity("Pay weight, whole load", "weight", { level: "more" }),
    price: f.money("Price", { level: "more" }),
    price_basis: f.enum("Price is", PRICE_BASIS, { level: "more" }),
    cause: f.text("Cause", { level: "more", help: "For deaths, when known" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const pen = label(e.subjects, "pen", x);
    const parts = [x.t("{{lot}}: {{head}} · {{reason}}", { lot: label(e.subjects, "lot", x), head: heads(d.head, x), reason: x.t(OUT_REASONS[d.reason]).toLowerCase() })];
    if (pen) parts.push(pen);
    if (d.pay_weight) parts.push(x.t("pay {{weight}}", { weight: x.quantity(d.pay_weight) }));
    if (d.cause) parts.push(d.cause);
    return parts.join(" · ");
  },
  review: "always",
  agent: {
    when: "Counted head left a feeding or stocker lot: shipped, sold, died, moved to another lot, or missing on a recount.",
    avoid: "For one tagged animal that died or sold, record the herd death or sale for that animal; add this only to take counted head out of the lot.",
    examples: ["lost one in pen 2 this morning, Davis steers", "shipped 80 head of the Davis steers, pay weight 812"],
  },
});

// ---------------------------------------------------------------- daily feeding

const call = defineEvent<CallData>({
  type: "feeding.call",
  label: "Bunk call",
  description: "The bunk reading and how much to feed a pen on a feeding day. A call is a plan; deliveries record what was fed.",
  icon: "feedCall",
  tone: "feed",
  subjects: [{ role: "pen", kind: "place", label: "Pen", required: true }],
  fields: {
    bunk: f.enum("Bunk", BUNK_SCORES, { agent: "slick = licked clean, crumbs = a few crumbs, thin = thin layer left, half = about half left, heavy = most left, untouched = not eaten." }),
    per_head: f.quantity("Call per head", "weight", {
      help: "As-fed pounds a head for the day",
      agent: "As-fed per head. For a change like \"bump 5 a head\", add it to the pen's last call per head from feeding_pens and mark evidence calculated.",
    }),
    amount: f.quantity("Call for the pen", "weight", { help: "As-fed total for the pen for the day", agent: "Whole-pen as-fed total, when said or figured from per head times head." }),
    change_per_head: f.quantity("Change per head", "weight", { level: "more", help: "What they said to change, like +5 or -2", agent: "The stated change, negative for a cut." }),
    ration: f.text("Ration", { level: "more", agent: "Ration name from feeding_pens or feeding_rations." }),
    ration_version_id: f.text("Ration version", { level: "more", agent: "Ration version id from feeding_pens or feeding_rations. Keep the pen's current one unless they name another ration." }),
    head: f.int("Head in the pen", { level: "more", min: 0, agent: "Head count used for per-head math, from feeding_pens." }),
    drops: f.int("Drops", { level: "more", min: 1, max: 6 }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const parts: string[] = [];
    const pen = label(e.subjects, "pen", x);
    if (d.amount && d.per_head) parts.push(x.t("{{pen}} called {{amount}} ({{perHead}} a head)", { pen, amount: x.quantity(d.amount), perHead: x.quantity(d.per_head) }));
    else if (d.amount) parts.push(x.t("{{pen}} called {{amount}}", { pen, amount: x.quantity(d.amount) }));
    else if (d.per_head) parts.push(x.t("{{pen}} called {{perHead}} a head", { pen, perHead: x.quantity(d.per_head) }));
    else parts.push(pen);
    if (d.change_per_head) parts.push(x.t("{{change}} a head", { change: `${d.change_per_head.value > 0 ? "+" : ""}${x.quantity(d.change_per_head)}` }));
    if (d.bunk) parts.push(x.t("bunk {{score}}", { score: x.t(BUNK_SCORES[d.bunk]).toLowerCase() }));
    if (d.ration) parts.push(d.ration);
    return parts.join(" · ");
  },
  agent: {
    when: "Someone read a bunk or set how much to feed a pen today or tomorrow (the call).",
    avoid: "Not for feed actually delivered (use delivery). The date is the feeding day the call is for.",
    examples: ["pen 4 bunk slick, bump 5 pounds a head", "pen 2 crumbs, hold the call at 470", "call pen 1 at 15 a head tomorrow"],
  },
  validate: (d) => (!d.bunk && !d.amount && !d.per_head && !d.change_per_head ? [{ path: "data.per_head", message: "Give a bunk reading or a call" }] : []),
});

const load = defineEvent<LoadData>({
  type: "feeding.load",
  label: "Mixer load",
  description: "A batch mixed in the feed truck or mixer: each ingredient's target and what actually went in. A confirmed load uses the ingredients from stock once.",
  icon: "feedLoad",
  tone: "feed",
  subjects: [{ role: "mixer", kind: "asset", label: "Truck or mixer" }],
  fields: {
    load_id: f.text("Load number", { agent: "Load number or ticket as spoken, e.g. 3 or L-12." }),
    ration: f.text("Ration", { level: "more" }),
    ration_version_id: f.text("Ration version", { required: true, agent: "Ration version id from feeding_rations." }),
    ingredients: f.list("Ingredients", f.object("Ingredient", {
      item_id: f.ref("Ingredient", "stock_item", { required: true }),
      target: f.quantity("Target", "weight"),
      actual: f.quantity("Loaded", "weight", { required: true }),
    }), { required: true, itemLabel: "Ingredient", agent: "Only ingredients with a stated loaded weight." }),
    batch: f.quantity("Batch size", "weight", { level: "more", help: "What the load was mixed for" }),
    left_over: f.quantity("Left in the mixer", "weight", { level: "more", help: "Feed still in the mixer after the last drop" }),
    waste: f.quantity("Thrown out", "weight", { level: "more" }),
    weights_from: f.enum("Weights from", { scale: "Scale", manual: "Written or read by eye" }, { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const total = d.ingredients.reduce((n, i) => n + (toLb(i.actual) ?? 0), 0);
    const parts = [d.load_id ? x.t("Load {{id}}", { id: d.load_id }) : x.t("Load")];
    parts.push(x.t("{{amount}} of {{ration}}", { amount: x.quantity({ value: Math.round(total), unit: "lb" }), ration: d.ration ?? x.t("feed") }));
    parts.push(x.count(d.ingredients.length, "one ingredient", "{{count}} ingredients"));
    return parts.join(" · ");
  },
  review: "always",
  agent: {
    when: "A mixer or feed-truck load was mixed, with the pounds of each ingredient loaded.",
    avoid: "Not for feed dropped in a pen (delivery) or for a call.",
    examples: ["load 3, 10,040 pounds of finisher: corn 6,020, silage 2,980, supplement 1,040"],
  },
  validate: (d) => (!d.ingredients?.length ? [{ path: "data.ingredients", message: "Add at least one ingredient" }] : []),
});

const delivery = defineEvent<DeliveryData>({
  type: "feeding.delivery",
  label: "Feed delivered",
  description: "Feed actually dropped in a pen's bunk, with its ration. Without a recorded mixer load, ingredients come out of stock by the ration's proportions.",
  icon: "truck",
  tone: "feed",
  subjects: [
    { role: "pen", kind: "place", label: "Pen", required: true },
    { role: "truck", kind: "asset", label: "Truck or mixer" },
  ],
  fields: {
    amount: f.quantity("Delivered", "weight", { required: true, help: "As fed", agent: "As-fed weight actually delivered. 0 only when the pen was skipped." }),
    ration: f.text("Ration", { level: "more", agent: "Ration name from feeding_pens or feeding_rations." }),
    ration_version_id: f.text("Ration version", { level: "more", agent: "Ration version id: the pen's current one from feeding_pens unless they named another ration (evidence linked)." }),
    load_id: f.text("Load number", { level: "more" }),
    drop: f.int("Drop", { level: "more", min: 1, max: 6 }),
    skip_reason: f.enum("Not fed because", SKIP_REASONS, { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const pen = label(e.subjects, "pen", x);
    if (!d.amount.value) return x.t("{{pen}} not fed: {{reason}}", { pen, reason: x.t(SKIP_REASONS[d.skip_reason ?? "other"]).toLowerCase() });
    const parts = [d.ration
      ? x.t("{{pen}} fed {{amount}} of {{ration}}", { pen, amount: x.quantity(d.amount), ration: d.ration })
      : x.t("{{pen}} fed {{amount}}", { pen, amount: x.quantity(d.amount) })];
    if (d.drop) parts.push(x.t("drop {{n}}", { n: d.drop }));
    if (d.load_id) parts.push(x.t("load {{id}}", { id: d.load_id }));
    return parts.join(" · ");
  },
  review: "always",
  agent: {
    when: "Feed was actually delivered (dropped, fed out) into a pen's bunk, or a pen was skipped.",
    avoid: "Not for the call (how much to feed) or hay put out on pasture without a pen ration.",
    examples: ["delivered 6,200 of finisher to pen 4", "fed pen 1 and pen 2, 430 each", "skipped pen 3, mixer broke down"],
  },
  validate: (d) => {
    const issues = [];
    if (d.amount.value < 0) issues.push({ path: "data.amount", message: "Delivered can't be below zero" });
    if (d.amount.value === 0 && !d.skip_reason) issues.push({ path: "data.skip_reason", message: "Say why the pen wasn't fed" });
    if (d.amount.value > 0 && !d.ration_version_id) issues.push({ path: "data.ration_version_id", message: "Pick the ration that was fed" });
    return issues;
  },
});

/** Hay, cubes, mineral or tubs fed to cattle on pasture or in a trap: everyday cow-calf feeding, outside any pen ration. */
interface SupplementData { feed?: string; feed_name?: string; amount: { value: number; unit: string }; per_head?: { value: number; unit: string }; head?: number; how?: string; notes?: string }
const FED_IN = { ground: "On the ground", ring: "Hay ring", feeder: "Feeder or bunk", tub: "Tub or lick", mineral_feeder: "Mineral feeder" } as const;
const supplement = defineEvent<SupplementData>({
  type: "feeding.supplement",
  label: "Fed on pasture",
  description: "Hay, cubes, cake, mineral or tubs put out for cattle on pasture or in a trap. Comes out of supplies when the feed is one of them.",
  icon: "hay",
  tone: "feed",
  subjects: [
    { role: "group", kind: "group", label: "Group" },
    { role: "place", kind: "place", label: "Where" },
    { role: "animal", kind: "animal", label: "Animals", many: true },
  ],
  fields: {
    feed: f.ref("Feed", "stock_item", { agent: "The hay, cubes, mineral or tub from supplies (find_records stock_item)." }),
    feed_name: f.text("Feed name", { level: "more", agent: "Only when the feed isn't in supplies, in the speaker's words." }),
    amount: f.quantity("Amount fed", "any", { required: true, help: "In the unit the feed is counted in: bales, bags, pounds", agent: "Use the feed's counting unit from supplies (bale, bag, lb...)." }),
    per_head: f.quantity("Per head", "any", { level: "more" }),
    head: f.int("Head fed", { level: "more", min: 0 }),
    how: f.enum("Fed in", FED_IN, { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const feed = d.feed ? x.label(d.feed) : d.feed_name ?? x.t("feed");
    const who = e.subjects.find((s) => s.role === "group")?.label ?? e.subjects.find((s) => s.role === "place")?.label;
    const amount = `${d.amount.value} ${d.amount.unit}`;
    return who ? x.t("{{who}} fed {{amount}} of {{feed}}", { who, amount, feed }) : x.t("Fed {{amount}} of {{feed}}", { amount, feed });
  },
  effects: {
    stock: (d) => (d.feed && d.amount.value > 0 ? [{ item_id: d.feed, delta: -d.amount.value, unit: d.amount.unit, reason: "use" as const }] : []),
  },
  agent: {
    when: "Hay, cubes, cake, mineral, protein tubs or other feed actually put out for cattle on pasture or in a trap.",
    avoid: "Not for a feedlot or backgrounding pen bunk (feeding.delivery), and not a plan to feed.",
    examples: ["fed the fall calvers 400 pounds of cubes", "put out 6 bales in North Pasture", "le echamos 4 bultos de mineral a las vacas"],
  },
  validate: (d) => (d.amount.value < 0 ? [{ path: "data.amount", message: "Amount can't be below zero" }] : []),
});

// ---------------------------------------------------------------- facets

const LOT_EVENTS = ["feeding.receive", "feeding.lot_move", "feeding.head_out"];

export const lotFacet = defineFacet<LotState>({
  id: "feeding",
  subject: "group",
  version: 1,
  eventTypes: LOT_EVENTS,
  reduce: (events, { today }) => reduceLot(events, today),
  facts: (s, { today }): Fact[] => {
    const out: Fact[] = [{ label: "Head on feed", value: msg("{{count}} head", { count: s.head }), icon: "feed", tone: "feed" }];
    if (s.in_on && s.head > 0) out.push({ label: "Days on feed", value: String(daysOnFeed(s.in_on, today)) });
    if (s.short > 0) out.push({ label: "Short at receiving", value: msg("{{count}} head", { count: s.short }), tone: "warn", icon: "warn" });
    return out;
  },
});

export const penFacet = defineFacet<PenState>({
  id: "feeding",
  subject: "place",
  version: 1,
  eventTypes: [...LOT_EVENTS, "feeding.call", "feeding.delivery"],
  reduce: (events) => reducePen(events),
  badges: (s, { today }): Badge[] => (s.last_delivery?.on === today ? [{ label: msg("Fed today"), tone: "feed", icon: "feed" }] : []),
  facts: (s): Fact[] => (s.ration?.name ? [{ label: "Ration", value: s.ration.name, icon: "feedRation" }] : []),
});

export const animalFacet = defineFacet<AnimalFeedState>({
  id: "feeding",
  subject: "animal",
  version: 1,
  eventTypes: ["feeding.receive", "feeding.head_out"],
  reduce: (events) => reduceAnimal(events),
});

// ---------------------------------------------------------------- stock kinds and settings

const FEED_FIELDS: Fields = {
  dm_pct: f.number("Dry matter", { min: 0, max: 100, suffix: "%", help: "Percent dry matter, as fed" }),
  cost_per_unit: f.number("Cost per unit", { min: 0, step: 0.0001, help: "What one unit costs, in the unit you count it in (lb, ton, bag)" }),
  unit_weight: f.quantity("Weight of one unit", "weight", { level: "more", help: "For bags, tubs or bales, so pounds fed can come out of stock" }),
  cp_pct: f.number("Crude protein", { level: "more", min: 0, max: 100, suffix: "%", help: "Dry-matter basis" }),
  tdn_pct: f.number("TDN", { level: "more", min: 0, max: 100, suffix: "%", help: "Dry-matter basis" }),
  nem: f.number("NEm", { level: "more", min: 0, suffix: "Mcal/cwt" }),
  neg: f.number("NEg", { level: "more", min: 0, suffix: "Mcal/cwt" }),
  ndf_pct: f.number("NDF", { level: "more", min: 0, max: 100, suffix: "%" }),
  analysis_on: f.date("Analysis date", { level: "more" }),
  analysis_ref: f.text("Lab report or tag", { level: "more" }),
};

const FEED_LOT_FIELDS: Fields = {
  dm_pct: f.number("Dry matter of this lot", { min: 0, max: 100, suffix: "%" }),
  sampled_on: f.date("Sampled", { level: "more" }),
  ticket: f.text("Scale ticket", { level: "more" }),
};

export const FEEDING_SETTINGS: Fields = {
  variance_pct: f.number("Flag deliveries off the call by more than", { min: 1, max: 50, suffix: "%", help: "Those show on Today" }),
  dof_warning_days: f.int("Warn before planned days on feed", { min: 0, max: 60, suffix: "days" }),
};

export default defineModule({
  id: "feeding",
  label: "Feeding",
  description: "Rations, feed inventory, bunk calls, deliveries, receiving and closeouts.",
  events: [receive, lotMove, headOut, call, load, delivery, supplement],
  facets: [lotFacet, penFacet, animalFacet],
  jobs: [{
    type: "feeding.closeout",
    label: "Lot closeout",
    description: "Close out a lot: head in and out, days on feed, feed, gain, cost of gain and owner statements.",
    icon: "report",
    tone: "feed",
    workspace: "feeding",
    subjectKind: "group",
    fields: {
      lot_id: f.ref("Lot", "group", { required: true }),
      through: f.date("Through", { help: "Last day counted" }),
      yardage_per_head_day: f.number("Yardage per head per day", { min: 0, step: 0.01 }),
      other_costs: f.list("Other costs", f.object("Cost", {
        what: f.text("What", { required: true }),
        category: f.enum("Kind", { health: "Health", freight: "Freight", interest: "Interest", marketing: "Marketing", other: "Other" }),
        amount: f.money("Amount", { required: true }),
      }), { itemLabel: "Cost" }),
      imported: f.object("Results from another system", {
        source: f.text("Reported by", { required: true, placeholder: "Yard software, consultant or buyer" }),
        as_of: f.date("Report date"),
        head_in: f.int("Head in", { min: 0 }),
        head_out: f.int("Head shipped", { min: 0 }),
        days_on_feed: f.number("Days on feed", { min: 0 }),
        adg_lb: f.number("ADG", { suffix: "lb" }),
        feed_to_gain: f.number("Feed:gain"),
        dm_to_gain: f.number("Dry matter:gain"),
        cost_of_gain_cwt: f.number("Cost of gain", { suffix: "/cwt" }),
      }, { level: "more", help: "Kept as reported, beside the figures from Hondo's records" }),
    },
    stages: [{ id: "draft", label: "Draft" }, { id: "final", label: "Final" }],
    agent: { when: "Someone asks to close out a lot (\"closeout the Davis lot\"). Put the lot's group id in data.lot_id and name it after the lot." },
  }],
  workspaces: [{
    id: "feeding",
    label: "Run feeding",
    description: "Today's feed sheet, receiving, lots on feed and closeouts.",
    icon: "feed",
    group: "care",
    keywords: ["feed", "feeding", "bunk", "call", "ration", "pen", "feedlot", "stocker", "backgrounding", "receiving", "delivery", "load", "mixer", "closeout", "lot"],
    keywordsEs: ["comedero", "ración", "alimentar", "corral", "engorda", "lectura", "repasto", "recepción", "mezcladora", "cierre", "lote"],
    presets: ["feedlot"],
  }],
  ranchSections: [{
    id: "rations",
    label: "Rations and feed",
    description: "Ration versions, feed on hand and days of feed left.",
    icon: "feedRation",
    group: "supplies",
    keywords: ["ration", "feed", "ingredient", "hay", "cubes", "dry matter"],
    keywordsEs: ["ración", "alimento", "ingrediente", "heno", "materia seca"],
  }],
  stockKinds: [
    { kind: "feed", label: "Feed", fields: FEED_FIELDS, lotFields: FEED_LOT_FIELDS },
    { kind: "ingredient", label: "Feed ingredient", fields: FEED_FIELDS, lotFields: FEED_LOT_FIELDS },
  ],
  settings: FEEDING_SETTINGS,
  messages: [
    // Validation and calculation notes produced by shared code.
    "Add at least one ingredient", "Ingredients must add up to 100%", "This ingredient is listed twice", "Enter a percentage above zero",
    "Dry matter must be between 0 and 100%", "Dry matter is needed for a dry-matter ration", "Say how much one unit weighs",
    "No head shipped yet, so there is no gain to measure.", "Pay weight in is missing on a receipt.", "Pay weight out is missing on a shipment.",
    "Gain is zero or negative, so feed:gain and cost of gain can't be figured.", "Dry matter is unknown for some feed, so dry-matter figures are blank.",
    // Validation from the contract and the server.
    "Say how many head went to each pen", "Head by pen must add up to head off the truck", "Every pen in the split must be one of the pens",
    "More tagged cattle than head off the truck", "Pick a different pen to move them to", "Give a bunk reading or a call", "Delivered can't be below zero",
    "Say why the pen wasn't fed", "Pick the ration that was fed", "Unknown ration version", "Pick a lot, not a herd or management group",
    "More head than this lot had in that pen that day", "More head than this lot had on feed that day", "Some of these cattle are already on feed in another lot",
    "An owner is listed twice", "More tagged cattle than head out", "Enter the weight loaded", "This delivery was just recorded. Give it a drop number if it's a second drop.",
    "Pick the day this version starts", "Unknown product", "Name the ration", "A ration with that name already exists",
    "A new version can't start before the version it replaces", "Pick a lot with feeding records", "Pick a date",
    // Server errors.
    "We couldn't find that ration.", "We couldn't find that closeout.", "This closeout isn't linked to a lot with feeding records.", "Reopen the closeout to change it.",
    "This closeout is already final.", "Fix what's listed before making it final.", "This closeout is already open.", "This lot has no feeding records yet.",
    // Labels the web looks up by key.
    "Not called", "Not fed yet", "Called, not fed", "Fed", "Skipped today", "Empty pen", "Through date", "Head in", "Head shipped", "Deaths", "Head days",
    "Feed, as fed", "Feed, dry matter", "ADG", "Feed cost", "Total cost",
  ],
});

export type { AnimalFeedState, BunkScore, CallData, DeliveryData, HeadOutData, LoadData, LotMoveData, LotState, PenState, ReceiveData };
